import type { ForwardedRef } from 'react';

import { forwardRef, useId } from 'react';

import type { TextFieldProps } from './types/Input.types';

import { Input } from '../Input';
import { TextFieldWrapper } from './TextFieldWrapper';

export const TextField = forwardRef((props: TextFieldProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    className,
    inputClassName,
    onChange,
    label,
    placeholder,
    value,
    error,
    readOnly = false,
    helperText,
    description,
    labelClassName,
    id: providedId,
    'aria-describedby': ariaDescribedBy,
    ...inputProps
  } = props;

  const generatedId = useId();
  const id = providedId || generatedId;

  const describedBy = [
    ariaDescribedBy,
    helperText && `${id}-helper`,
    error && `${id}-error`,
    label && description && `${id}-description`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TextFieldWrapper
      className={className}
      error={error}
      label={label}
      readOnly={readOnly}
      helperText={helperText}
      description={description}
      required={props.required}
      labelClassName={labelClassName}
      id={id}
    >
      <Input
        ref={ref}
        id={id}
        className={inputClassName}
        onChange={onChange}
        placeholder={!readOnly ? placeholder : ''}
        value={value}
        error={error}
        aria-describedby={describedBy || undefined}
        aria-invalid={!!error}
        disabled={props.disabled || readOnly}
        readOnly={readOnly}
        {...inputProps}
      />
    </TextFieldWrapper>
  );
});
